import { getContext } from "@icg360/pi-syntax-tools";
import * as dateFns from "date-fns";
import { referenceTables } from "../reference-tables";
import { convertToDate, mapTermFormatToFormatter } from "../utils/date-utils";
import { validateType } from "../utils/type-validation";
import { tryExecSyntaxForExpression } from "./execute-syntax";
export function handleValidation(state, exp, iterableIndex) {
    const { model } = referenceTables[state.key];
    const term = getTerm(state, exp.termName, exp.iterableName, iterableIndex);
    const context = getContext({
        env: state.env,
        meta: model.meta,
        terms: state.terms,
        iterables: state.iterables,
        this: exp.iterableName && iterableIndex !== undefined
            ? {
                index: iterableIndex,
                position: iterableIndex + 1,
                term,
                iterable: state.iterables[exp.iterableName],
                terms: state.iterables[exp.iterableName].instances[iterableIndex].terms,
            }
            : {
                term,
            },
    });
    if (term.value && term.separator) {
        const values = term.value
            .split(term.separator)
            .map(v => v.trim())
            .filter(Boolean)
            .reverse();
        let isValid = true;
        let currentValue = values.pop();
        while (isValid && currentValue) {
            isValid = validateEachValue(term, currentValue, context, exp, iterableIndex);
            currentValue = values.pop();
        }
    }
    else {
        validateEachValue(term, term.value, context, exp, iterableIndex);
    }
}
function validateEachValue(term, value, context, exp, iterableIndex) {
    if (exp.type === "validation" && exp.valueType === "date") {
        return dateValidation(term, value, exp, context, iterableIndex);
    }
    let isValid = true;
    for (const [index, validator] of exp.validators.entries()) {
        if (validator.type === "date-range-min" ||
            validator.type === "date-range-max") {
            throw new Error(`Invalid validator type "${validator.type}" for non-date validation`);
        }
        const output = Number(tryExecSyntaxForExpression(validator.syntax, context, exp, iterableIndex));
        if (!term.validation[index]) {
            term.validation[index] = {
                type: validator.type,
                value: output,
                pass: true,
            };
        }
        else {
            term.validation[index].value = output;
        }
        switch (validator.type) {
            case "length-min":
                if (value && value.length < output) {
                    isValid = false;
                    term.validation[index].pass = false;
                }
                else {
                    term.validation[index].pass = true;
                }
                break;
            case "length-max":
                if (value && value.length > output) {
                    isValid = false;
                    term.validation[index].pass = false;
                }
                else {
                    term.validation[index].pass = true;
                }
                break;
            case "amount-min":
                if (value && Number(value) < output) {
                    isValid = false;
                    term.validation[index].pass = false;
                }
                else {
                    term.validation[index].pass = true;
                }
                break;
            case "amount-max":
                if (value && Number(value) > output) {
                    isValid = false;
                    term.validation[index].pass = false;
                }
                else {
                    term.validation[index].pass = true;
                }
                break;
            default:
                throw new Error(`Unknown validator type: ${validator.type}`);
        }
    }
    // Add an extra validation item to the list based on enums or the term value type (e.g. phone, email, etc.)
    const extraValidatorIndex = exp.validators.length;
    if (term.enums) {
        let passEnums = true;
        if (value &&
            !term.enums.some(e => (e.isRelevant ? value === e.value : false))) {
            passEnums = false;
        }
        if (!term.validation[extraValidatorIndex]) {
            term.validation[extraValidatorIndex] = {
                type: "enum-option",
                value: undefined,
                pass: passEnums,
            };
        }
        else {
            term.validation[extraValidatorIndex].pass = passEnums;
        }
        if (!passEnums) {
            isValid = false;
        }
    }
    else {
        const typeValidationPass = validateType(exp.valueType, value);
        if (typeValidationPass !== undefined) {
            if (!term.validation[extraValidatorIndex]) {
                term.validation[extraValidatorIndex] = {
                    type: "value-type",
                    value: exp.valueType,
                    pass: typeValidationPass,
                };
            }
            else {
                term.validation[extraValidatorIndex].pass = typeValidationPass;
            }
            if (!typeValidationPass) {
                isValid = false;
            }
        }
    }
    term.isValid = isValid;
    return isValid;
}
function getTerm(state, termName, iterableName, iterableIndex) {
    if (iterableName && iterableIndex !== undefined) {
        return state.iterables[iterableName].instances[iterableIndex].terms[termName];
    }
    return state.terms[termName];
}
function dateValidation(term, value, exp, context, iterableIndex) {
    let isValid = true;
    const dateValue = convertToDate(value);
    const validDate = dateValue !== null;
    for (const [index, validator] of exp.validators.entries()) {
        if (validator.type !== "date-range-min" &&
            validator.type !== "date-range-max") {
            throw new Error(`Invalid validator type "${validator.type}" for date validation`);
        }
        const output = convertToDate(tryExecSyntaxForExpression(validator.syntax, context, exp, iterableIndex));
        const dateStr = mapTermFormatToFormatter(term.format)(output);
        if (!output) {
            throw new Error(`Invalid value for validator "${validator.type}" in "${term.name}": ${dateStr}`);
        }
        if (!term.validation[index]) {
            term.validation[index] = {
                type: validator.type,
                value: dateStr,
                pass: true,
            };
        }
        else {
            term.validation[index].value = dateStr;
        }
        if (!validDate) {
            term.validation[index].pass = true;
            continue;
        }
        switch (validator.type) {
            case "date-range-min":
                // This is a little weird, but we want to allow the user to enter the
                // min date, but the min date may include a specific time of the day
                // we want to ignore, so we need to check if the date is not after
                // the day before the min date
                if (dateValue &&
                    !dateFns.isAfter(dateValue, dateFns.add(output, { days: -1 }))) {
                    isValid = false;
                    term.validation[index].pass = false;
                }
                else {
                    term.validation[index].pass = true;
                }
                break;
            case "date-range-max":
                if (dateValue && dateFns.isAfter(dateValue, output)) {
                    isValid = false;
                    term.validation[index].pass = false;
                }
                else {
                    term.validation[index].pass = true;
                }
                break;
            default:
                throw new Error(`Unknown validator type: "${validator.type}"`);
        }
    }
    const extraValidatorIndex = exp.validators.length;
    if (!term.validation[extraValidatorIndex]) {
        term.validation[extraValidatorIndex] = {
            type: "value-type",
            value: "date",
            pass: validDate,
        };
    }
    else {
        term.validation[extraValidatorIndex].pass = validDate;
    }
    term.isValid = validDate && isValid;
    return validDate && isValid;
}
//# sourceMappingURL=term-validation.js.map